<div class="w-full space-y-6">
  <!-- Header Section -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">User Sessions</h1>
      <p class="text-gray-600 mt-1">Monitor active user sessions and activities</p>
    </div>
    <p-button class="bg-sky-500 text-white px-4 py-2 rounded-md"
      label="Refresh Sessions" 
      icon="pi pi-refresh">
    </p-button>
  </div>

  <!-- Stats Cards -->
  <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Sessions</p>
          <p class="text-2xl font-bold text-gray-800">{{ getTotalSessions() }}</p>
        </div>
        <div class="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-desktop text-sky-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Active Sessions</p>
          <p class="text-2xl font-bold text-green-600">{{ getTotalActiveSessions() }}</p>
        </div>
        <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-check-circle text-green-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Unique Users</p>
          <p class="text-2xl font-bold text-blue-600">{{ getUniqueUsers() }}</p>
        </div>
        <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-users text-blue-600 text-xl"></i>
        </div>
      </div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Active Tenants</p>
          <p class="text-2xl font-bold text-purple-600">{{ getUniqueTenants() }}</p>
        </div>
        <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
          <i class="pi pi-building text-purple-600 text-xl"></i>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters and Search -->
  <div class="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <!-- Search -->
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Search Sessions</label>
        <div class="relative">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            placeholder="Search by user name or tenant..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <i class="pi pi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>

      <!-- Status Filter -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
        <select
          [(ngModel)]="filterStatus"
          class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option *ngFor="let option of statusOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>

      <!-- Tenant Filter -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Filter by Tenant</label>
        <select
          [(ngModel)]="filterTenant"
          class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option *ngFor="let option of tenantOptions" [value]="option.value">{{ option.label }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Sessions List -->
  <div class="bg-white rounded-xl shadow-lg border border-sky-100 overflow-hidden">
    <div class="px-6 py-4 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-100">
      <h2 class="text-lg font-semibold text-gray-800">Sessions ({{ filteredSessions.length }})</h2>
    </div>
    
    <div class="divide-y divide-gray-200">
      <div 
        *ngFor="let session of filteredSessions" 
        class="p-6 hover:bg-sky-50 transition-colors cursor-pointer"
        (click)="selectSession(session)"
      >
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-4">
              <!-- User Avatar -->
              <p-avatar 
                [label]="getInitials(session.firstName, session.lastName)"
                styleClass="bg-sky-100 text-sky-600 text-lg font-semibold"
                size="large"
                shape="circle">
              </p-avatar>
              
              <!-- Session Details -->
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-2">
                  <h3 class="text-lg font-semibold text-gray-800">{{ session.firstName }} {{ session.lastName }}</h3>
                  <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusBadgeClass(session.sessionStatus)">
                    {{ session.sessionStatus }}
                  </span>
                </div>
                
                <div class="flex items-center space-x-4 text-sm text-gray-600">
                  <span class="flex items-center">
                    <i class="pi pi-building mr-1"></i>
                    {{ session.tenantName }}
                  </span>
                  <span class="flex items-center">
                    <i class="pi pi-clock mr-1"></i>
                    Started: {{ formatDateTime(session.sessionStarted) }}
                  </span>
                  <span *ngIf="session.lastActivity" class="flex items-center">
                    <i class="pi pi-history mr-1"></i>
                    Duration: {{ getSessionDuration(session.sessionStarted, session.lastActivity) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Session Info Preview -->
          <div class="hidden md:block">
            <div class="text-sm text-gray-600">
              <span *ngIf="session.ipAddress" class="block">{{ session.ipAddress }}</span>
              <span *ngIf="session.location" class="block text-xs">{{ session.location }}</span>
            </div>
          </div>
          
          <!-- Arrow Icon -->
          <div class="ml-4">
            <i class="pi pi-chevron-right text-gray-400"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Right Side Drawer -->
<div 
  class="fixed inset-0 z-50 overflow-hidden"
  [class.hidden]="!drawerOpen"
>
  <!-- Backdrop -->
  <div 
    class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
    (click)="closeDrawer()"
  ></div>
  
  <!-- Drawer -->
  <div class="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform">
    <div class="flex flex-col h-full">
      <!-- Drawer Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold">Session Details</h2>
          <button 
            (click)="closeDrawer()"
            class="p-2 hover:bg-sky-600 rounded-lg transition-colors"
          >
            <i class="pi pi-times text-xl"></i>
          </button>
        </div>
      </div>
      
      <!-- Drawer Content -->
      <div class="flex-1 overflow-y-auto p-6" *ngIf="selectedSession">
        <!-- Session Info -->
        <div class="mb-6">
          <div class="flex items-center space-x-4 mb-4">
            <p-avatar 
              [label]="getInitials(selectedSession.firstName, selectedSession.lastName)"
              styleClass="bg-sky-100 text-sky-600 text-2xl font-semibold"
              size="xlarge"
              shape="circle">
            </p-avatar>
            <div>
              <h3 class="text-2xl font-bold text-gray-800">{{ selectedSession.firstName }} {{ selectedSession.lastName }}</h3>
              <p class="text-gray-600">{{ selectedSession.tenantName }}</p>
            </div>
          </div>
          
          <div class="flex space-x-2">
            <span [class]="'px-3 py-1 rounded-full text-sm font-medium ' + getStatusBadgeClass(selectedSession.sessionStatus)">
              {{ selectedSession.sessionStatus }}
            </span>
          </div>
        </div>

        <!-- Session Timing -->
        <div class="bg-sky-50 p-4 rounded-lg mb-6">
          <h4 class="font-semibold text-gray-800 mb-3">Session Timing</h4>
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600">Started:</span>
              <span class="font-medium">{{ formatDateTime(selectedSession.sessionStarted) }}</span>
            </div>
            <div *ngIf="selectedSession.lastActivity" class="flex justify-between">
              <span class="text-gray-600">Last Activity:</span>
              <span class="font-medium">{{ formatDateTime(selectedSession.lastActivity) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Duration:</span>
              <span class="font-medium">{{ getSessionDuration(selectedSession.sessionStarted, selectedSession.lastActivity) }}</span>
            </div>
          </div>
        </div>

        <!-- Device Information -->
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div *ngIf="selectedSession.ipAddress">
            <h4 class="font-semibold text-gray-800 mb-1">IP Address</h4>
            <p class="text-gray-600 text-sm">{{ selectedSession.ipAddress }}</p>
          </div>
          <div *ngIf="selectedSession.location">
            <h4 class="font-semibold text-gray-800 mb-1">Location</h4>
            <p class="text-gray-600 text-sm">{{ selectedSession.location }}</p>
          </div>
          <div *ngIf="selectedSession.deviceInfo" class="md:col-span-2">
            <h4 class="font-semibold text-gray-800 mb-1">Device</h4>
            <p class="text-gray-600 text-sm">{{ selectedSession.deviceInfo }}</p>
          </div>
        </div>

        <!-- User Agent -->
        <div class="mb-6" *ngIf="selectedSession.userAgent">
          <h4 class="font-semibold text-gray-800 mb-2">User Agent</h4>
          <div class="p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 break-all">{{ selectedSession.userAgent }}</p>
          </div>
        </div>

        <!-- Session ID -->
        <div class="mb-6">
          <h4 class="font-semibold text-gray-800 mb-1">Session ID</h4>
          <p class="text-gray-600 text-sm font-mono">{{ selectedSession.sessionId }}</p>
        </div>

        <!-- Actions -->
        <div class="space-y-3">
          <button 
            class="w-full bg-sky-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-sky-600 transition-colors"
            *ngIf="selectedSession.sessionStatus === 'active'"
          >
            Extend Session
          </button>
          <button 
            class="w-full bg-red-100 text-red-700 py-3 px-4 rounded-lg font-semibold hover:bg-red-200 transition-colors"
            *ngIf="selectedSession.sessionStatus === 'active'"
            (click)="terminateSession(selectedSession)"
          >
            Terminate Session
          </button>
          <button 
            class="w-full bg-orange-100 text-orange-700 py-3 px-4 rounded-lg font-semibold hover:bg-orange-200 transition-colors"
            (click)="terminateAllSessions(selectedSession.userId)"
          >
            Terminate All User Sessions
          </button>
        </div>
      </div>
    </div>
  </div>
</div>